import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Form, Container } from "react-bootstrap";
import { connect } from "react-redux";
import { isEmpty } from "lodash";
import MaterialTable from "material-table";
import Select from "react-select";
import moment from "moment";
import Button from '@mui/material/Button';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { setHeading } from "../../../accounts/account.actions";
import { GlobalSpinner } from "../../../../../global/globalFunctions";
import { GetConstructionOrderApi } from "../ConstructionOrder/ConstructionOrderAction";
import { GetConstructionOrderPaymentApi, GenerateOrderPaymentLedgerPDF, resetConstructionOrderPaymentData } from "./ConstructionOrderPaymentAction";


const styles = {
    card: {
        border: "1px solid #ddd",
        borderRadius: "12px",
        padding: "10px",
        textAlign: "center",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        backgroundColor: "#81ABD3",
        flex: 1,
    },
    container: {
        display: "flex",
        gap: "15px",
        margin: "20px 0",
    },
    heading: {
        fontSize: "15px",
        fontWeight: "bold",
        marginBottom: "10px",
    },
    data: {
        fontSize: "13px",
        fontWeight: "600",
        color: "#333",
    },
};

const ConstructionOrderPaymentLedger = (props) => {
    const [orderId, setOrderId] = useState("");
    const [showLedger, setShowLedger] = useState(false);

    const orderOptions = useMemo(() => {
        return isEmpty(props.constructionOrderDataList) ? [] : props.constructionOrderDataList.map((x) => ({
            value: x._id,
            label: `Order # ${x.sequenceNumber} - ${x.customerDetails?.name || ''}`,
            order: x,
        }));
    }, [props.constructionOrderDataList]);
    const selectedOrderOption = useMemo(() => {
        return orderOptions.find((opt) => opt.value === orderId) || null;
    }, [orderId, orderOptions]);
    const selectedOrder = selectedOrderOption?.order;

    const handleChangeOrder = (selectedOption) => {
        setOrderId(selectedOption.value);
        setShowLedger(false);
        props.resetConstructionOrderPaymentData();
    };

    const handleShowLedger = () => {
        props.GetConstructionOrderPaymentApi(orderId);
        setShowLedger(true);
    };

    const handlePrintPDF = () => {
        props.GenerateOrderPaymentLedgerPDF(orderId, selectedOrder);
    };

    const orderAmount = Number(selectedOrder?.sellingPrice || 0);

    const sortedLedgerData = useMemo(() => {
        if (isEmpty(props.constructionOrderPaymentList)) return [];
        const sorted = [...props.constructionOrderPaymentList].sort((a, b) => new Date(a.date) - new Date(b.date));
        let cumulative = 0;
        return sorted.map((item) => {
            const previousReceived = cumulative;
            cumulative = Math.round((cumulative + Number(item.payment || 0)) * 100) / 100;
            return {
                ...item,
                previousReceived,
                closingBalance: cumulative,
            };
        });
    }, [props.constructionOrderPaymentList]);

    const totalReceived = Math.round(sortedLedgerData.reduce((sum, item) => sum + Number(item.payment || 0), 0) * 100) / 100;
    const remainingBalance = Math.round((orderAmount - totalReceived) * 100) / 100;

    const ledgerCards = [
        { title: "Order #", data: `${selectedOrder?.sequenceNumber || '-'}` },
        { title: "Customer Name", data: `${selectedOrder?.customerDetails?.name || '-'}` },
        { title: "Order Amount", data: `${orderAmount}` },
        { title: "Total Received", data: `${totalReceived}` },
        { title: "Remaining Balance", data: `${remainingBalance}` },
    ];

    useEffect(() => {
        props.setHeading("Order Payment Ledger");
        props.GetConstructionOrderApi();
        props.resetConstructionOrderPaymentData();
    }, []);

    if (props.isFetchingConstructionOrder) {
        return <GlobalSpinner />;
    }

    return (
        <Container fluid>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label><b>Select Order *</b></Form.Label>
                        <Select
                            isDisabled={isEmpty(orderOptions)}
                            value={selectedOrderOption}
                            placeholder="Select Order"
                            onChange={handleChangeOrder}
                            options={orderOptions}
                        />
                    </Form.Group>
                </Col>
            </Row>

            <div style={{ marginTop: '10px', marginBottom: '10px', display: 'flex', gap: '15px' }}>
                <Button
                    variant="contained"
                    color="error"
                    startIcon={<PictureAsPdfIcon />}
                    size="large"
                    disabled={isEmpty(orderId)}
                    onClick={handlePrintPDF}
                >
                    Print PDF Ledger
                </Button>
                <Button
                    variant="contained"
                    startIcon={<VisibilityIcon />}
                    size="large"
                    disabled={isEmpty(orderId)}
                    onClick={handleShowLedger}
                >
                    Show Ledger
                </Button>
            </div>

            {showLedger && (
                props.isFetchingConstructionOrderPayment ? (
                    <GlobalSpinner />
                ) : (
                    <>
                        <div style={styles.container}>
                            {ledgerCards.map((card, index) => (
                                <div key={index} style={styles.card}>
                                    <div style={styles.heading}>{card.title}</div>
                                    <div style={styles.data}>{card.data}</div>
                                </div>
                            ))}
                        </div>

                        <div className="main">
                            <MaterialTable
                                title="Order Payment Ledger"
                                columns={[
                                    { title: 'S.No', field: 'sequenceNumber' },
                                    { title: 'Date', field: 'date', render: (rowData) => moment(rowData.date).format("DD-MM-YYYY") },
                                    { title: 'Payment Mode', field: 'paymentMode' },
                                    { title: 'Description', field: 'description' },
                                    { title: 'Payment', field: 'payment' },
                                    // { title: 'Previous Received', field: 'previousReceived' },
                                    { title: 'Closing Balance', field: 'closingBalance' },
                                    { title: 'Created By', field: 'userDetails.userName' },
                                ]}
                                data={sortedLedgerData}
                                options={{
                                    actionsColumnIndex: -1,
                                    exportButton: true,
                                    exportAllData: true,
                                    paging: true,
                                    pageSize: 50,
                                    emptyRowsWhenPaging: false,
                                    pageSizeOptions: [50, 100, 150, 200],
                                    headerStyle: {
                                        position: "sticky",
                                        top: 0,
                                        color: "#00BBBB",
                                        fontWeight: "550",
                                        onRowAdd: "none",
                                    },
                                }}
                            />
                        </div>
                    </>
                )
            )}
        </Container>
    );
};

const mapDispatchToProps = (dispatch) => {
    return {
        GetConstructionOrderApi: () => dispatch(GetConstructionOrderApi()),
        GetConstructionOrderPaymentApi: (orderId) => dispatch(GetConstructionOrderPaymentApi(orderId)),
        GenerateOrderPaymentLedgerPDF: (orderId, orderData) => dispatch(GenerateOrderPaymentLedgerPDF(orderId, orderData)),
        resetConstructionOrderPaymentData: () => dispatch(resetConstructionOrderPaymentData()),
        setHeading: (data) => dispatch(setHeading(data)),
    };
};
const mapStateToProps = (state, ownProps) => ({
    isFetchingConstructionOrder: state.ConstructionOrderReducer.isFetchingConstructionOrder,
    constructionOrderDataList: state.ConstructionOrderReducer.constructionOrderDataList,
    isFetchingConstructionOrderPayment: state.ConstructionOrderPaymentReducer.isFetchingConstructionOrderPayment,
    constructionOrderPaymentList: state.ConstructionOrderPaymentReducer.constructionOrderPaymentList,
});

export default connect(mapStateToProps, mapDispatchToProps)(ConstructionOrderPaymentLedger);
